// src/utils/emailTemplates.ts
import { directSupabase } from '../lib/supabaseClient';
import userTemplates from '../data/user-email-templates.json';
import type { Database } from '../types/supabase';

export type TemplateType = Database['public']['Tables']['email_templates']['Row']['template_type'];
export type EmailTemplate = Database['public']['Tables']['email_templates']['Row'];
export type Json = Database['public']['Tables']['email_templates']['Row']['calendar_event_settings'];

export interface CalendarEventSettings {
  subject: string | null;
  body: string | null;
  duration_minutes: number | null;
  location: string | null;
  attendees: Array<{
    emailAddress: {
      address: string;
      name: string;
    };
    type: string;
  }> | null;
}

export type TemplateData = {
  template: string;
  subject: string;
  is_default?: boolean;
  is_active?: boolean;
  calendar_event_settings: CalendarEventSettings | null;
};

export type EmailTemplates = {
  [K in TemplateType]: TemplateData;
};

const initialCalendarEventSettings: CalendarEventSettings = {
  subject: null,
  body: null,
  duration_minutes: null,
  location: null,
  attendees: null
};

// Default templates with calendar event settings
const defaultTemplates: EmailTemplates = {
  glassOrder: {
    template: userTemplates.glassOrder.template,
    subject: userTemplates.glassOrder.subject,
    calendar_event_settings: initialCalendarEventSettings
  },
  windowOrder: {
    template: userTemplates.windowOrder.template,
    subject: userTemplates.windowOrder.subject,
    calendar_event_settings: initialCalendarEventSettings
  },
  keystoneIntroduction: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  },
  firstSurveyAppointmentRequest: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  },
  firstSurveyAppointmentConfirmation: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  },
  sendCustomerEstimate: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  },
  finalSurveyAppointmentRequest: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  },
  finalSurveyAppointmentConfirmation: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  },
  orderGoods: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  },
  installationDateConfirmation: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  },
  deliveryDateConfirmation: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  },
  otherKeystoneServices: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  },
  completionEmail: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  },
  requestReview: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  },
  newsletter: {
    template: '',
    subject: '',
    calendar_event_settings: initialCalendarEventSettings
  }
};

// File persistence constants
const EMAIL_TEMPLATES_KEY = 'emailTemplates';
const GLASS_EMAIL_TEMPLATE_KEY = 'emailTemplate';
const GLASS_EMAIL_SUBJECT_KEY = 'emailSubject';
const WINDOW_EMAIL_TEMPLATE_KEY = 'windowOrderEmailTemplate';
const WINDOW_EMAIL_SUBJECT_KEY = 'windowOrderEmailSubject';

// Function to save updated templates to localStorage
export const saveUserTemplates = (templates: EmailTemplates): void => {
  try {
    // Store in localStorage
    localStorage.setItem(EMAIL_TEMPLATES_KEY, JSON.stringify(templates));
    console.log('Email templates saved successfully');
    
    // Save individual templates for backward compatibility
    localStorage.setItem(GLASS_EMAIL_TEMPLATE_KEY, templates.glassOrder.template);
    localStorage.setItem(GLASS_EMAIL_SUBJECT_KEY, templates.glassOrder.subject);
    localStorage.setItem(WINDOW_EMAIL_TEMPLATE_KEY, templates.windowOrder.template);
    localStorage.setItem(WINDOW_EMAIL_SUBJECT_KEY, templates.windowOrder.subject);
  } catch (error) {
    console.error('Error saving templates:', error);
  }
};

// Function to save glass order template
export const saveGlassOrderTemplate = (template: string, subject: string): void => {
  try {
    // Save to individual keys for backward compatibility
    localStorage.setItem(GLASS_EMAIL_TEMPLATE_KEY, template);
    localStorage.setItem(GLASS_EMAIL_SUBJECT_KEY, subject);
    
    // Load current templates and update
    const templates = loadTemplates();
    templates.glassOrder = { 
      subject, 
      template,
      calendar_event_settings: templates.glassOrder.calendar_event_settings || initialCalendarEventSettings
    };
    
    // Save updated templates
    saveUserTemplates(templates);
  } catch (error) {
    console.error('Error saving glass order template:', error);
  }
};

// Function to save window order template
export const saveWindowOrderTemplate = (template: string, subject: string): void => {
  try {
    // Save to individual keys for backward compatibility
    localStorage.setItem(WINDOW_EMAIL_TEMPLATE_KEY, template);
    localStorage.setItem(WINDOW_EMAIL_SUBJECT_KEY, subject);
    
    // Load current templates and update
    const templates = loadTemplates();
    templates.windowOrder = { 
      subject, 
      template,
      calendar_event_settings: templates.windowOrder.calendar_event_settings || initialCalendarEventSettings
    };
    
    // Save updated templates
    saveUserTemplates(templates);
  } catch (error) {
    console.error('Error saving window order template:', error);
  }
};

// Function to load all templates, with fallbacks
export const loadTemplates = (): EmailTemplates => {
  try {
    // First try to load from localStorage
    const storedTemplates = localStorage.getItem(EMAIL_TEMPLATES_KEY);
    if (storedTemplates) {
      return JSON.parse(storedTemplates);
    }
    
    // If not in localStorage, build from individual template keys or defaults
    const glassTemplate = localStorage.getItem(GLASS_EMAIL_TEMPLATE_KEY) || userTemplates.glassOrder.template;
    const glassSubject = localStorage.getItem(GLASS_EMAIL_SUBJECT_KEY) || userTemplates.glassOrder.subject;
    const windowTemplate = localStorage.getItem(WINDOW_EMAIL_TEMPLATE_KEY) || userTemplates.windowOrder.template;
    const windowSubject = localStorage.getItem(WINDOW_EMAIL_SUBJECT_KEY) || userTemplates.windowOrder.subject;
    
    const templates: EmailTemplates = {
      glassOrder: {
        template: glassTemplate,
        subject: glassSubject,
        calendar_event_settings: initialCalendarEventSettings
      },
      windowOrder: {
        template: windowTemplate,
        subject: windowSubject,
        calendar_event_settings: initialCalendarEventSettings
      },
      keystoneIntroduction: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      firstSurveyAppointmentRequest: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      firstSurveyAppointmentConfirmation: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      sendCustomerEstimate: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      finalSurveyAppointmentRequest: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      finalSurveyAppointmentConfirmation: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      orderGoods: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      installationDateConfirmation: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      deliveryDateConfirmation: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      otherKeystoneServices: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      completionEmail: {
        template: '',
        subject: '',
        calendar_event_settings: initialCalendarEventSettings
      },
      requestReview: {
        template: '',
        subject: '',
        calendar_event_settings: initialCalendarEventSettings
      },
      newsletter: {
        template: '',
        subject: '',
        calendar_event_settings: initialCalendarEventSettings
      }
    };
    
    // Save the templates to ensure consistent storage
    saveUserTemplates(templates);
    
    return templates;
  } catch (error) {
    console.error('Error loading templates:', error);
    
    // Fall back to default templates if something goes wrong
    return {
      glassOrder: {
        ...userTemplates.glassOrder,
        calendar_event_settings: initialCalendarEventSettings
      },
      windowOrder: {
        ...userTemplates.windowOrder,
        calendar_event_settings: initialCalendarEventSettings
      },
      keystoneIntroduction: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      firstSurveyAppointmentRequest: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      firstSurveyAppointmentConfirmation: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      sendCustomerEstimate: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      finalSurveyAppointmentRequest: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      finalSurveyAppointmentConfirmation: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      orderGoods: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      installationDateConfirmation: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      deliveryDateConfirmation: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      otherKeystoneServices: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      completionEmail: {
        template: '',
        subject: '',
        calendar_event_settings: initialCalendarEventSettings
      },
      requestReview: {
        template: '',
        subject: '',
        calendar_event_settings: initialCalendarEventSettings
      },
      newsletter: {
        template: '',
        subject: '',
        calendar_event_settings: initialCalendarEventSettings
      }
    };
  }
};

// Helper function to load glass order template
export const loadGlassOrderTemplate = (): { template: string, subject: string } => {
  try {
    // First try to load from user templates
    if (userTemplates?.glassOrder?.template && userTemplates.glassOrder.subject) {
      let template = userTemplates.glassOrder.template;
      template = ensureProperHtmlStructure(template, 'Glass Order');
      return {
        template: template,
        subject: userTemplates.glassOrder.subject
      };
    }

    // Fall back to localStorage if not in user templates
    const savedTemplate = localStorage.getItem('glassOrderEmailTemplate');
    const savedSubject = localStorage.getItem('glassOrderEmailSubject');
    
    if (savedTemplate && savedSubject) {
      const template = ensureProperHtmlStructure(savedTemplate, 'Glass Order');
      return {
        template: template,
        subject: savedSubject
      };
    }
    
    // Fall back to default templates
    let template = defaultTemplates.glassOrder.template;
    template = ensureProperHtmlStructure(template, 'Glass Order');
    return {
      template: template,
      subject: defaultTemplates.glassOrder.subject
    };
  } catch (error) {
    console.error('Error loading glass order template:', error);
    const template = ensureProperHtmlStructure(defaultTemplates.glassOrder.template, 'Glass Order');
    return {
      template: template,
      subject: defaultTemplates.glassOrder.subject
    };
  }
};

// Helper function to load window order template
export const loadWindowOrderTemplate = (): { template: string, subject: string } => {
  try {
    // First try to load from user templates
    if (userTemplates?.windowOrder?.template && userTemplates.windowOrder.subject) {
      let template = userTemplates.windowOrder.template;
      template = ensureProperHtmlStructure(template, 'Window Order');
      return {
        template: template,
        subject: userTemplates.windowOrder.subject
      };
    }
    
    // Fall back to localStorage if not in user templates
    const savedTemplate = localStorage.getItem('windowOrderEmailTemplate');
    const savedSubject = localStorage.getItem('windowOrderEmailSubject');
    
    if (savedTemplate && savedSubject) {
      const template = ensureProperHtmlStructure(savedTemplate, 'Window Order');
      return {
        template: template,
        subject: savedSubject
      };
    }
    
    // Fall back to default templates
    let template = defaultTemplates.windowOrder.template;
    template = ensureProperHtmlStructure(template, 'Window Order');
    return {
      template: template,
      subject: defaultTemplates.windowOrder.subject
    };
  } catch (error) {
    console.error('Error loading window order template:', error);
    // Even in error, ensure proper HTML structure
    const template = ensureProperHtmlStructure(defaultTemplates.windowOrder.template, 'Window Order');
    return {
      template: template,
      subject: defaultTemplates.windowOrder.subject
    };
  }
};

// Helper function to ensure proper HTML structure in templates
function ensureProperHtmlStructure(template: string, defaultTitle: string = 'Order Email'): string {
  // Check if the template already has basic HTML structure
  if (
    template.trim().toLowerCase().includes('<!doctype html>') ||
    template.trim().toLowerCase().includes('<html') 
  ) {
    // Template already has HTML structure
    return template;
  }
  
  // If not, wrap the template in a proper HTML structure
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${defaultTitle}</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 20px; color: #333; }
    img { max-width: 100%; height: auto; }
    table { border-collapse: collapse; width: 100%; }
    th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
    th { background-color: #f2f2f2; }
  </style>
</head>
<body>
${template}
</body>
</html>`;
}

// Function to reset templates to defaults
export const resetTemplatesToDefaults = (): void => {
  try {
    // Reset templates to the current user templates from JSON
    saveUserTemplates({
      glassOrder: {
        ...userTemplates.glassOrder,
        calendar_event_settings: initialCalendarEventSettings
      },
      windowOrder: {
        ...userTemplates.windowOrder,
        calendar_event_settings: initialCalendarEventSettings
      },
      keystoneIntroduction: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      firstSurveyAppointmentRequest: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      firstSurveyAppointmentConfirmation: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      sendCustomerEstimate: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      finalSurveyAppointmentRequest: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      finalSurveyAppointmentConfirmation: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      orderGoods: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      installationDateConfirmation: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      deliveryDateConfirmation: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      otherKeystoneServices: { 
        template: '', 
        subject: '', 
        calendar_event_settings: initialCalendarEventSettings 
      },
      completionEmail: {
        template: '',
        subject: '',
        calendar_event_settings: initialCalendarEventSettings
      },
      requestReview: {
        template: '',
        subject: '',
        calendar_event_settings: initialCalendarEventSettings
      },
      newsletter: {
        template: '',
        subject: '',
        calendar_event_settings: initialCalendarEventSettings
      }
    });
    
    // Clear individual keys to ensure consistent state
    localStorage.removeItem(GLASS_EMAIL_TEMPLATE_KEY);
    localStorage.removeItem(GLASS_EMAIL_SUBJECT_KEY);
    localStorage.removeItem(WINDOW_EMAIL_TEMPLATE_KEY);
    localStorage.removeItem(WINDOW_EMAIL_SUBJECT_KEY);
    
    console.log('Email templates reset to JSON templates');
  } catch (error) {
    console.error('Error resetting templates:', error);
  }
};

// Email template utilities
// This file contains shared utilities for email templates

// Common template variables that can be used across different email types
export const COMMON_TEMPLATE_VARIABLES = [
  // Project Variables
  { name: 'projectName', description: 'Name of the project' },
  { name: 'projectReference', description: 'Project reference number' },
  
  // Customer Variables
  { name: 'customerName', description: 'Customer name' },
  { name: 'customerEmail', description: 'Customer email address' },
  { name: 'customerPhone', description: 'Customer phone number' },
  { name: 'customerCompany', description: 'Customer company name (optional)' },
  { name: 'customerAddress', description: 'Customer address (optional)' },
  { name: 'customerNotes', description: 'Additional notes from customer (optional)' },
  
  // Company Variables
  { name: 'companyName', description: 'Your company name' },
  { name: 'companyPhone', description: 'Your company phone number' },
  { name: 'companyEmail', description: 'Your company email address' },
  { name: 'companyAddress', description: 'Your company address' },
  
  // Order Variables
  { name: 'orderNumber', description: 'The unique order number' },
  { name: 'orderDate', description: 'The date the order was created' },
  { name: 'customerReference', description: 'Customer-provided reference number' },
  { name: 'subtotal', description: 'Order subtotal before VAT' },
  { name: 'vat', description: 'VAT amount' },
  { name: 'total', description: 'Total order amount' },
  
  // Survey Variables
  { name: 'surveyDate', description: 'Raw survey date (YYYY-MM-DD)' },
  { name: 'surveyTime', description: 'Raw survey time (HH:MM)' },
  { name: 'surveyDateLong', description: 'Formatted survey date (e.g., "Tuesday, 21st May 2024")' },
  
  // Supplier Variables
  { name: 'supplierName', description: 'Supplier company name' },
  { name: 'supplierContactName', description: 'Supplier contact person name' },
  { name: 'supplierEmail', description: 'Supplier email address' },
  { name: 'supplierPhone', description: 'Supplier phone number' },
  { name: 'supplierReference', description: 'Supplier reference number' },
  { name: 'supplierNotes', description: 'Additional notes for supplier' },
  
  // Installation Variables
  { name: 'installationDate', description: 'Raw installation date (YYYY-MM-DD)' },
  { name: 'installationTime', description: 'Raw installation time (HH:MM)' },
  { name: 'installationDateLong', description: 'Formatted installation date (e.g., "Tuesday, 21st May 2024")' },
  { name: 'installationNotes', description: 'Additional installation notes' },

  // Public document / messaging variables
  { name: 'secureUrl', description: 'Public document URL (legacy placeholder)' },
  { name: 'shareUrl', description: 'Public document URL alias' },
  { name: 'publicDocUrl', description: 'Public document URL for customer correspondence' },
  { name: 'referralUrl', description: 'Public referral page URL for refer-a-friend CTA' },
  { name: 'unsubscribeLink', description: 'Direct newsletter unsubscribe URL for recipient' },
  { name: 'unsubscribeWidget', description: 'Pre-built newsletter unsubscribe footer widget HTML' },
  { name: 'replyMessage', description: 'Staff reply text added to the public document thread' }
];

// Helper function to validate template variables
export const validateTemplateVariables = (template: string, allowedVariables: string[]): string[] => {
  const variableRegex = /{{([^{}]+)}}/g;
  const foundVariables = new Set<string>();
  let match;

  while ((match = variableRegex.exec(template)) !== null) {
    const variable = match[1].trim();
    // Skip block markers (#, ^, /)
    if (!variable.startsWith('#') && !variable.startsWith('^') && !variable.startsWith('/')) {
      foundVariables.add(variable);
    }
  }

  return Array.from(foundVariables).filter(v => !allowedVariables.includes(v));
};

// Helper function to format template variables for display
export const formatTemplateVariables = (variables: { name: string; description: string }[]): string => {
  return variables.map(v => `{{${v.name}}} - ${v.description}`).join('\n');
};

// Helper function to get all template variables
export const getAllTemplateVariables = (): { name: string; description: string }[] => {
  return [
    ...COMMON_TEMPLATE_VARIABLES,
    ...FIRST_SURVEY_APPOINTMENT_REQUEST_VARIABLES,
    ...FINAL_SURVEY_APPOINTMENT_REQUEST_VARIABLES,
    ...SURVEY_APPOINTMENT_CONFIRMATION_VARIABLES
  ].filter((v, i, a) => a.findIndex(t => t.name === v.name) === i); // Remove duplicates
};

// First Survey Appointment Request template variables
export const FIRST_SURVEY_APPOINTMENT_REQUEST_VARIABLES = [
  { name: 'projectName', description: 'Name of the project' },
  { name: 'customerName', description: 'Customer name' },
  { name: 'customerEmail', description: 'Customer email address' },
  { name: 'customerPhone', description: 'Customer phone number' },
  { name: 'companyName', description: 'Company name' },
  { name: 'companyPhone', description: 'Company phone number' }
];

// Final Survey Appointment Request template variables
export const FINAL_SURVEY_APPOINTMENT_REQUEST_VARIABLES = [
  { name: 'projectName', description: 'Name of the project' },
  { name: 'customerName', description: 'Customer name' },
  { name: 'customerEmail', description: 'Customer email address' },
  { name: 'customerPhone', description: 'Customer phone number' },
  { name: 'companyName', description: 'Company name' },
  { name: 'companyPhone', description: 'Company phone number' }
];

// Survey Appointment Confirmation template variables
export const SURVEY_APPOINTMENT_CONFIRMATION_VARIABLES = [
  { name: 'projectName', description: 'Name of the project' },
  { name: 'customerName', description: 'Customer name' },
  { name: 'customerEmail', description: 'Customer email address' },
  { name: 'customerPhone', description: 'Customer phone number' },
  { name: 'companyName', description: 'Company name' },
  { name: 'companyPhone', description: 'Company phone number' },
  { name: 'surveyDate', description: 'Raw survey date (YYYY-MM-DD)' },
  { name: 'surveyTime', description: 'Raw survey time (HH:MM)' },
  { name: 'surveyDateLong', description: 'Formatted survey date (e.g., "Tuesday, 21st May 2024")' }
];

export const emailTemplates = {
  async getAll() {
    const { data, error } = await directSupabase
      .from('email_templates')
      .select('*')
      .order('template_type');
    
    return { data, error };
  },

  async create({
    template_type,
    subject,
    template,
    version,
    is_default,
    is_active,
    calendar_event_settings
  }: {
    template_type: TemplateType;
    subject: string;
    template: string;
    version: number;
    is_default: boolean;
    is_active: boolean;
    calendar_event_settings: CalendarEventSettings | null;
  }) {
    const { data, error } = await directSupabase
      .from('email_templates')
      .insert([{
        template_type,
        subject,
        template,
        version,
        is_default,
        is_active,
        calendar_event_settings
      }])
      .select()
      .single();
    
    return { data, error };
  },

  async setDefault({
    template_type,
    subject,
    template
  }: {
    template_type: TemplateType;
    subject: string;
    template: string;
  }) {
    // First, set all templates of this type to not default
    await directSupabase
      .from('email_templates')
      .update({ is_default: false })
      .eq('template_type', template_type);
    
    // Then create the new default template
    const { data, error } = await directSupabase
      .from('email_templates')
      .insert([{
        template_type,
        subject,
        template,
        version: 1,
        is_default: true,
        is_active: true,
        calendar_event_settings: null
      }])
      .select()
      .single();
    
    return { data, error };
  }
}; 